import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import { Wordmark } from "@/components/brand/Wordmark";
import { MobileMenu } from "@/components/marketing/MobileMenu";
import { BRAND_NAME } from "@/lib/brand";
import { appUrl, env } from "@/lib/env";
import { isNavActive, localizedPath, navItems, preserveSearch } from "@/lib/nav";
import { useDesktopPlatform } from "@/lib/useDesktopPlatform";

const topBarCtaClassName =
  "inline-flex h-11 items-center rounded-full bg-primary px-3 text-xs font-semibold text-primary-foreground transition-all duration-300 hover:scale-[1.03] hover:bg-primary-press hover:shadow-glow-accent active:scale-[0.97] min-[360px]:px-4 min-[360px]:text-sm sm:h-9 sm:px-5";

// Navbar — one solid full-width bar (no capsules, no border, no
// translucency): brand (+ links) on the left, waitlist CTA + mobile menu on
// the right. Pages place it themselves (the home hero pins it above the
// video; LocaleLayout sticks it above interior pages).

export function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const items = navItems(env.launched, useDesktopPlatform() !== "unknown");
  const referralCode = new URLSearchParams(location.search).get("ref");

  return (
    <motion.nav
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="relative z-20 w-full bg-background"
      aria-label={t("nav.primaryAria")}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3.5 sm:px-6 sm:py-4">
        <div className="flex min-w-0 items-center gap-8">
          <Link
            to={preserveSearch(localizedPath(location.pathname, "/"), location.search)}
            aria-label={`${BRAND_NAME} home`}
            className="shrink-0"
          >
            <Wordmark tile />
          </Link>
          <div className="hidden items-center gap-7 text-sm font-medium md:flex">
            {items.map((item) => {
              if (item.path === "/") return null;
              const active = isNavActive(location.pathname, item.path);
              return (
                <Link
                  key={item.key}
                  to={preserveSearch(localizedPath(location.pathname, item.path), location.search)}
                  className={
                    active
                      ? "text-white transition-colors duration-300"
                      : "text-white/70 transition-colors duration-300 hover:text-white"
                  }
                >
                  {t(item.labelKey)}
                </Link>
              );
            })}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          {env.launched ? (
            <a href={appUrl("/sign-in", referralCode)} className={topBarCtaClassName}>
              {t("nav.signIn")}
            </a>
          ) : (
            <Link to={preserveSearch("/#waitlist", location.search)} className={topBarCtaClassName}>
              {t("nav.joinWaitlist")}
            </Link>
          )}
          <MobileMenu />
        </div>
      </div>
    </motion.nav>
  );
}
